import { inject, Service, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ICodetableItem, IExternePartnerZuordnungDto, IPartner, IUser } from '@mng/mgs-lib';
import { UserService } from '../../user/user.service';
import { executeActionOnLogout } from '../../mgs-action-on-logout';
import { MgsWebApi } from '../../mgs-webapi.service';
import { PartnerService } from '../partner.service';
import { AuthStateFacade } from '../../auth/auth-state.facade';

@Service()
export class PartnerDetailService {
  readonly userService = inject(UserService);
  readonly authState = inject(AuthStateFacade);
  readonly webApi = inject(MgsWebApi);
  readonly partnerService = inject(PartnerService);

  #partner = signal<IPartner | undefined>(undefined);
  partner = this.#partner.asReadonly();

  #partnerUsers = signal<IUser[] | undefined>(undefined);
  partnerUsers = this.#partnerUsers.asReadonly();

  #externePartnerZuordnungen = signal<IExternePartnerZuordnungDto[] | undefined>(undefined);
  externePartnerZuordnungen = this.#externePartnerZuordnungen.asReadonly();

  bundeslaender: ICodetableItem[];

  constructor() {
    executeActionOnLogout(() => this.reset());
  }

  async resolvePartner(id: number): Promise<IPartner> {
    const response = await firstValueFrom(this.webApi.partner(id));
    this.#partner.set(response.data.result);
    this.#partnerUsers.set(undefined);
    this.#externePartnerZuordnungen.set(undefined);
    return this.#partner();
  }

  neuerPartner() {
    const partner: IPartner = {
      labordatenAustausch: false,
      labordatenUebernahme: false,
      leistungGesperrt: true,
      honorarEinsichtVerboten: false,
      extern: false,
      hatExternePartner: false,
      leistungRelevant: false,
      inaktiv: false
    };
    this.#partner.set(partner);
    this.#partnerUsers.set(undefined);
  }

  async resolvePartnerUsers(): Promise<void> {
    if (!this.#partnerUsers()) {
      this.#partnerUsers.set(await this.userService.resolveUserList(this.#partner().id));
    }
  }

  async resolveExternePartnerZuordnungen(force = false): Promise<void> {
    if (force || !this.#externePartnerZuordnungen()) {
      const zuordnungen = await firstValueFrom(this.webApi.externePartnerZuordnungenFuerPartner(this.#partner().id));
      this.#externePartnerZuordnungen.set(zuordnungen.data.result);
    }
  }

  async externePartnerZuordnungLoeschen(zuordnungId: number): Promise<void> {
    await firstValueFrom(this.webApi.externePartnerZuordnungLoeschen(zuordnungId));
    await this.resolveExternePartnerZuordnungen(true);
  }

  async externePartnerZuordnungAnlegen(externePartnerId: number): Promise<IExternePartnerZuordnungDto> {
    const zuordnung = (
      await firstValueFrom(
        this.webApi.externePartnerZuordnungAnlegen({
          partnerId: this.#partner().id,
          externePartnerId
        })
      )
    ).data.result;
    await this.resolveExternePartnerZuordnungen(true);
    return zuordnung;
  }

  async refreshPartnerUsers() {
    this.#partnerUsers.set(undefined);
    await this.resolvePartnerUsers();
  }

  reset() {
    this.#partner.set(undefined);
  }

  async partnerSpeichern(partner: IPartner) {
    const gespeicherterPartner = (await firstValueFrom(this.webApi.partnerSpeichern(partner))).data.result;
    this.#partner.set(gespeicherterPartner);
    this.partnerService.resetPartnerList();
  }

  async resolveBundeslaender(): Promise<ICodetableItem[]> {
    if (!this.bundeslaender) {
      const response = await firstValueFrom(this.webApi.bundeslaender());
      this.bundeslaender = response.data.result;
    }
    return this.bundeslaender;
  }
}
